/** @format */

import React from 'react';
import config from 'config';
import { Switch, Route } from 'react-router-dom';
import RoutedComponent from 'components/RoutedComponent';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
/** Containers sorted according to the registration flow order */

/**
 * Registration views
 */

import HomePage from 'views/Registration/HomePage';
import Register, { REGISTER_TYPE } from 'views/Registration/Register';
import Welcome from 'views/Onboarding/Welcome';
import FMHub from 'views/Onboarding/FMHub';
import Agreement from 'views/Onboarding/Agreement';
import SignUp from 'views/Onboarding/SignUp';
import ContactInfo from 'views/Onboarding/Onboarding/ContactInfo';

/**
 * Common views
 */
import PageNotFound from 'views/Static/PageNotFound';
import BrokerProfile from 'views/Common/BrokerProfile';
// import ObjectListing from 'views/Common/ObjectListing';
import ViewImages from 'views/Common/ViewImages';
import About from 'views/Common/About';
import FAQ from 'views/Common/FAQ';
import Contact from 'views/Common/Contact';
import ArticlePage from 'views/Common/ArticlePage';
import ArticlePage2 from 'views/Common/ArticlePage2';
import ArticlePage3 from 'views/Common/ArticlePage3';
import BrokerHidden from 'views/Static/BrokerHidden';

/**
 * Onboarding views
 */
import BillingAddress from 'views/Onboarding/Onboarding/BillingAddress';
import ActiveAreas from 'views/Onboarding/Onboarding/ActiveAreas';
import ProfilePicture from 'views/Onboarding/Onboarding/ProfilePicture';
import Presentation from 'views/Onboarding/Onboarding/Presentation';
import USP from 'views/Onboarding/Onboarding/USP';
import YourServices from 'views/Onboarding/Onboarding/MyServices';

/**
 * Salary Calculator view
 */
import SalaryCalculator from 'views/Registration/SalaryCalculatorPage';
/**
 * Leads Generator view
 */
import { LeadsGeneratorPage } from 'components/LeadsGenerator';
import SearchPage from 'views/Search';

/**
 * Consultant Pages
 */
import ConsultantPages, {
  ConsultPageModes
} from 'views/Registration/ConsultantPages';

import { SellPage, BuyPage, EvaluatePage } from 'views/Leads';

import { checkIsQALocalEnv } from 'utils';

/**
 * VisningsklarPage Leads page
 */
// import VisningsklarPage from 'views/Leads/Visningsklar';

import {
  VisningsklarArticlePage,
  RenovationArticlePage,
  InvestArticlePage,
  MonitorArticlePage,
  BuildPropertyArticlePage,
  Fm30ArticlePage,
  LeadsServicePage,
  TestLeadsServicePage
} from 'views/Common/Articles';

import FmLightLogo from 'assets/images/logos/logo-fria-maklare-light.svg';
import HomePageImage from 'assets/images/images/image-homepage-1.png';

const {
  constants: { flows, views, flowOrigins }
} = config;

const commonProps = {
  flow: flows.registration,
  flowOrigin: flowOrigins.registration
};

export default function Routes() {
  const { t } = useTranslation();
  const seoCommonTitle = t('lang_common:seo.title.common.text');
  const seoDescription = t('lang_common:seo.title.common.description.text');

  return (
    <>
      <Helmet>
        <title>{seoCommonTitle} </title>
        <link rel='canonical' href={window.location.origin} />

        <meta name='title' content={seoCommonTitle} />
        <meta name='description' content={seoDescription} />

        {/* Open Graph / Facebook */}
        <meta property='og:type' content='website' />
        <meta property='og:url' content={window.location.origin} />
        <meta property='og:title' content={seoCommonTitle} />
        <meta property='og:description' content={seoDescription} />
        <meta property='og:image' content={HomePageImage} />

        {/* Twitter */}
        <meta property='twitter:card' content='summary_large_image' />
        <meta property='twitter:url' content={window.location.origin} />
        <meta property='twitter:title' content={seoCommonTitle} />
        <meta property='twitter:description' content={seoDescription} />
        <meta property='twitter:image' content={HomePageImage} />

        <link rel='preload' as='image' href={FmLightLogo} />
      </Helmet>
      <Switch>
        {/*
         * Beginning of registration flow
         */}
        <RoutedComponent /*** HomePage ***/
          exact={true}
          isPrivateRoute={false}
          Component={HomePage}
          view={views.homepage}
          path={t(`lang_route:registration_homepage`)}
          {...commonProps}
        />

        <RoutedComponent /*** Register page ***/
          exact={true}
          isPrivateRoute={false}
          Component={Register}
          view={views.register}
          path={t(`lang_route:registration_register`)}
          {...commonProps}
        />

        <RoutedComponent /*** Register broker page ***/
          exact={true}
          isPrivateRoute={false}
          Component={HomePage}
          view={views.homepage}
          registerType={REGISTER_TYPE.BROKER}
          path={t(`lang_route:registration_register_broker`)}
          {...commonProps}
        />
        <RoutedComponent /*** Register seller page ***/
          exact={true}
          isPrivateRoute={false}
          Component={HomePage}
          view={views.homepage}
          registerType={REGISTER_TYPE.SELLER}
          path={t(`lang_route:registration_register_seller`)}
          {...commonProps}
        />
        <RoutedComponent /*** Register buyer page ***/
          exact={true}
          isPrivateRoute={false}
          Component={HomePage}
          view={views.homepage}
          registerType={REGISTER_TYPE.BUYER}
          path={t(`lang_route:registration_register_buyer`)}
          {...commonProps}
        />

        {/* <RoutedComponent 
          exact={true}
          isPrivateRoute={false}
          Component={Register}
          view={views.register}
          registerType={REGISTER_TYPE.BROKER}
          path={t(`lang_route:registration_register_broker`)}
          {...commonProps}
        /> */}
        <RoutedComponent /*** Register buyer page ***/
          exact={true}
          isPrivateRoute={false}
          Component={Register}
          view={views.register}
          registerType={REGISTER_TYPE.BUYER}
          path={t(`lang_route:registration_register_buyer`)}
          {...commonProps}
        />
        <RoutedComponent /*** Register seller page ***/
          exact={true}
          isPrivateRoute={false}
          Component={Register}
          view={views.register}
          registerType={REGISTER_TYPE.SELLER}
          path={t(`lang_route:registration_register_seller`)}
          {...commonProps}
        />

        <RoutedComponent /*** Salary Calculator page ***/
          exact={true}
          isPrivateRoute={false}
          Component={SalaryCalculator}
          view={views.homepage}
          path={t(`lang_route:registration_salarycalculator`)}
          {...commonProps}
        />

        <RoutedComponent /*** Leads generator page ***/
          exact={true}
          isPrivateRoute={false}
          Component={LeadsGeneratorPage}
          view={views.homepage}
          path={t(`lang_route:registration_leads_page`)}
          {...commonProps}
        />
        <RoutedComponent /*** search objects page ***/
          exact={true}
          isPrivateRoute={false}
          Component={SearchPage}
          view={views.homepage}
          path={t(`lang_route:search`)}
          {...commonProps}
        />

        <RoutedComponent /*** view Sell leads page ***/
          exact={true}
          isPrivateRoute={false}
          Component={SellPage}
          view={views.homepage}
          path={t(`lang_route:leads_sell_page`)}
          {...commonProps}
        />
        <RoutedComponent /*** view Buy leads page ***/
          exact={true}
          isPrivateRoute={false}
          Component={BuyPage}
          view={views.homepage}
          path={t(`lang_route:leads_buy_page`)}
          {...commonProps}
        />

        <RoutedComponent /*** view Evaluate leads page ***/
          exact={true}
          isPrivateRoute={false}
          Component={EvaluatePage}
          view={views.homepage}
          path={t(`lang_route:leads_evaluate_page`)}
          {...commonProps}
        />

        <RoutedComponent /*** Abroad brokers register page ***/
          exact={true}
          isPrivateRoute={false}
          Component={Register}
          view={views.register}
          path={t(`lang_route:registration_abroad_brokers_page`)}
          registerType={REGISTER_TYPE.ABROAD}
          {...commonProps}
        />

        <RoutedComponent /*** intresseanmalan_nyheter2025 brokers register page ***/
          exact={true}
          isPrivateRoute={false}
          Component={Register}
          view={views.register}
          path={t(`lang_route:registration_intresseanmalan_nyheter2025_page`)}
          registerType={REGISTER_TYPE.INTRESSEANMALAN2025}
          {...commonProps}
        />

        <RoutedComponent /*** visningklar form  page ***/
          exact={true}
          isPrivateRoute={false}
          Component={ConsultantPages}
          view={views.register}
          path={t(`lang_route:registration_contact_leads_page`)}
          mode={ConsultPageModes.CONTACT_LEADS}
          {...commonProps}
        />

        {/*
         * END of registration flow
         */}
        {/*
         * Beginning of onboarding flow
         */}
        <RoutedComponent
          exact={true}
          isPrivateRoute={false}
          Component={Welcome}
          view={views.welcome}
          path={t(`lang_route:onboarding_welcome`)}
          flow={flows.onboarding}
          flowOrigin={flowOrigins.onboarding}
        />
        <RoutedComponent
          exact={true}
          isPrivateRoute={false}
          Component={FMHub}
          view={views.fmhub}
          path={t(`lang_route:onboarding_fmhub`)}
          flow={flows.onboarding}
          flowOrigin={flowOrigins.onboarding}
        />
        <RoutedComponent
          exact={true}
          isPrivateRoute={false}
          Component={Agreement}
          view={views.agreement}
          path={t(`lang_route:onboarding_agreement`)}
          flow={flows.onboarding}
          flowOrigin={flowOrigins.onboarding}
        />
        <RoutedComponent
          exact={true}
          isPrivateRoute={false}
          Component={SignUp}
          view={views.signup}
          path={t(`lang_route:onboarding_signup`)}
          flow={flows.onboarding}
          flowOrigin={flowOrigins.onboarding}
        />
        <RoutedComponent
          exact={true}
          isPrivateRoute={false}
          Component={ContactInfo}
          view={views.contactinfo}
          path={t(`lang_route:onboarding_contactinfo`)}
          flow={flows.onboarding}
          flowOrigin={flowOrigins.onboarding}
        />
        <RoutedComponent
          exact={true}
          isPrivateRoute={false}
          Component={BillingAddress}
          view={views.billingaddress}
          path={t(`lang_route:onboarding_billingaddress`)}
          flow={flows.onboarding}
          flowOrigin={flowOrigins.onboarding}
        />
        <RoutedComponent
          exact={true}
          isPrivateRoute={false}
          Component={ActiveAreas}
          view={views.activeareas}
          path={t(`lang_route:onboarding_activeareas`)}
          flow={flows.onboarding}
          flowOrigin={flowOrigins.onboarding}
        />
        <RoutedComponent
          exact={true}
          isPrivateRoute={false}
          Component={ProfilePicture}
          view={views.profilepicture}
          path={t(`lang_route:onboarding_profilepicture`)}
          flow={flows.onboarding}
          flowOrigin={flowOrigins.onboarding}
        />
        <RoutedComponent
          exact={true}
          isPrivateRoute={false}
          Component={Presentation}
          view={views.presentation}
          path={t(`lang_route:onboarding_presentation`)}
          flow={flows.onboarding}
          flowOrigin={flowOrigins.onboarding}
        />
        <RoutedComponent
          exact={true}
          isPrivateRoute={false}
          Component={USP}
          view={views.usp}
          path={t(`lang_route:onboarding_usp`)}
          flow={flows.onboarding}
          flowOrigin={flowOrigins.onboarding}
        />
        <RoutedComponent
          exact={true}
          isPrivateRoute={false}
          Component={YourServices}
          view={views.yourservices}
          path={t(`lang_route:onboarding_yourservices`)}
          flow={flows.onboarding}
          flowOrigin={flowOrigins.onboarding}
        />
        {/*
         * END of onboarding flow
         */}
        {/**
         * common flows
         */}
        <RoutedComponent /*** Contact page ***/
          exact={true}
          isPrivateRoute={false}
          Component={Contact}
          view={views.contact}
          path={t(`lang_route:registration_contact`)}
          flow={flows.common}
          flowOrigin={flowOrigins.common}
        />
        <RoutedComponent /*** view broker profile page ***/
          exact={false}
          isPrivateRoute={false}
          Component={BrokerProfile}
          view={views.broker_profile}
          path={t(`lang_route:common_broker_profile`)}
          flow={flows.common}
          flowOrigin={flowOrigins.common}
        />

        {/*** view object listings page ***/}

        {/* <RoutedComponent
          exact={false}
          isPrivateRoute={false}
          Component={ObjectListing}
          view={views.object_listing}
          path={t(`lang_route:common_object_listing`) + '/:vitecObjectId?'}
          flow={flows.common}
          flowOrigin={flowOrigins.common}
        /> */}
        <Route
          path={t(`lang_route:common_object_listing`) + '/:vitecObjectId?'}
          render={({ match }) => {
            const { vitecObjectId } = match.params;
            console.log(
              '----process.env.REACT_APP_BUILD_ENV',
              process.env.REACT_APP_BUILD_ENV
            );
            if (checkIsQALocalEnv()) {
              window.location.replace(
                `https://qa-app.friamaklare.se/objekt/${vitecObjectId}`
              );
            } else {
              window.location.replace(
                `https://app.friamaklare.se/objekt/${vitecObjectId}`
              );
            }

            return null;
          }}
        />

        <RoutedComponent /*** view object images page ***/
          exact={false}
          isPrivateRoute={false}
          Component={ViewImages}
          view={views.object_view_images}
          path={t(`lang_route:common_object_view_images`)}
          flow={flows.common}
          flowOrigin={flowOrigins.common}
        />
        <RoutedComponent /*** About page ***/
          exact={true}
          isPrivateRoute={false}
          Component={About}
          view={views.about}
          path={t(`lang_route:registration_about`)}
          flow={flows.common}
          flowOrigin={flowOrigins.common}
        />
        <RoutedComponent /*** Article page ***/
          exact={true}
          isPrivateRoute={false}
          Component={ArticlePage}
          view={views.article}
          path={t(`lang_route:registration_article`)}
          flow={flows.common}
          flowOrigin={flowOrigins.common}
        />
        <RoutedComponent /*** Article page 2s ***/
          exact={true}
          isPrivateRoute={false}
          Component={ArticlePage2}
          view={views.article2}
          path={t(`lang_route:registration_article2`)}
          flow={flows.common}
          flowOrigin={flowOrigins.common}
        />
        <RoutedComponent /*** Article page 2s ***/
          exact={true}
          isPrivateRoute={false}
          Component={ArticlePage3}
          view={views.article3}
          path={t(`lang_route:registration_article3`)}
          flow={flows.common}
          flowOrigin={flowOrigins.common}
        />
        <RoutedComponent /*** Visningsklar Article page ***/
          exact={true}
          isPrivateRoute={false}
          Component={VisningsklarArticlePage}
          view={views.visningsklar}
          path={t(`lang_route:leads_visningsklar_page`)}
          flow={flows.common}
          flowOrigin={flowOrigins.common}
        />
        <RoutedComponent /*** FM30 Article page ***/
          exact={true}
          isPrivateRoute={false}
          Component={Fm30ArticlePage}
          view={views.fm_30_service}
          path={t(`lang_route:leads_fm30_service_page`)}
          flow={flows.common}
          flowOrigin={flowOrigins.common}
        />
        <RoutedComponent /*** Renovation Article page ***/
          exact={true}
          isPrivateRoute={false}
          Component={RenovationArticlePage}
          view={views.renovation_object}
          path={t(`lang_route:registration_renovate_page`)}
          flow={flows.common}
          flowOrigin={flowOrigins.common}
        />
        <RoutedComponent /*** Invest Article page ***/
          exact={true}
          isPrivateRoute={false}
          Component={InvestArticlePage}
          view={views.invest_service}
          path={t(`lang_route:registration_invest_page`)}
          flow={flows.common}
          flowOrigin={flowOrigins.common}
        />
        <RoutedComponent /*** Monitor Article page ***/
          exact={true}
          isPrivateRoute={false}
          Component={MonitorArticlePage}
          view={views.monitor_service}
          path={t(`lang_route:registration_monitor_page`)}
          flow={flows.common}
          flowOrigin={flowOrigins.common}
        />
        <RoutedComponent /*** Build property Article page ***/
          exact={true}
          isPrivateRoute={false}
          Component={BuildPropertyArticlePage}
          view={views.build_property_service}
          path={t(`lang_route:registration_build_property_page`)}
          flow={flows.common}
          flowOrigin={flowOrigins.common}
        />
        <RoutedComponent /*** Leads service V2 Article page ***/
          exact={true}
          isPrivateRoute={false}
          Component={LeadsServicePage}
          view={views.leads_v2_service}
          path={t(`lang_route:leads_v2_service_page`)}
          flow={flows.common}
          flowOrigin={flowOrigins.common}
        />
        <RoutedComponent /*** test service V2 Article page ***/
          exact={true}
          isPrivateRoute={false}
          Component={TestLeadsServicePage}
          view={views.leads_v2_service}
          path={t(`lang_route:leads_test_service_page`)}
          flow={flows.common}
          flowOrigin={flowOrigins.common}
        />
        <RoutedComponent /*** FAQ page ***/
          exact={true}
          isPrivateRoute={false}
          Component={FAQ}
          view={views.faq}
          path={t(`lang_route:registration_faq`)}
          flow={flows.common}
          flowOrigin={flowOrigins.common}
        />

        <Route
          path={`${t(`lang_route:registration_homepage`)}404`}
          exact={true}>
          <PageNotFound flow={flows.common} view={views.page_not_found} />
        </Route>

        <Route
          path={`${t(`lang_route:registration_homepage`)}hidden`}
          exact={true}>
          <BrokerHidden flow={flows.common} view={views.broker_hidden} />
        </Route>

        {/*** view broker profile page ***/}
        {/* <RoutedComponent 
          exact={true}
          isPrivateRoute={false}
          Component={BrokerProfile}
          view={views.broker_profile}
          path={`${t(`lang_route:registration_homepage`)}:profileSlug`}
          flow={flows.common}
          flowOrigin={flowOrigins.common}
        /> */}

        <Route
          path={`${t(`lang_route:registration_homepage`)}:profileSlug`}
          render={({ match }) => {
            const { profileSlug } = match.params;

            if (checkIsQALocalEnv()) {
              window.location.replace(
                `https://qa-profil.friamaklare.se/broker/${profileSlug}`
              );
            } else {
              window.location.replace(
                `https://profil.friamaklare.se/broker/${profileSlug}`
              );
            }

            return null;
          }}
        />

        <Route>
          <PageNotFound flow={flows.common} view={views.page_not_found} />
        </Route>
      </Switch>
    </>
  );
}
